import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, FormControl, Glyphicon, Label, Table } from "react-bootstrap";
import {
    assignCapToUnit,
    createCapForUnit,
    createUnitNote,
    getCapsAdminOnly,
    getUnits,
    getUnitHealthLog,
    getUnitNotes,
    getUnitSim,
    patchCapUpdates,
    patchUnit,
    replaceUnit,
    unactivateCap,
} from "../utils/bedrockAPI";
import { formatPhoneNumber } from "../utils/Common";
import CapModal from "../components/CapModal";
import UnitSimPanel from "./UnitSimPanel";
import UnitPortField from "./UnitPortField";
import {isHardwareSim} from "../utils/simDisplay";
import {
    STATUS_OPTIONS,
    apiErrorMessage,
    formatDate,
    formatDateOnly,
    formatTimezoneDisplay,
    formatUpdateAvailableDate,
    findBySticker,
    healthStyle,
    httpsKeysDisplay,
    softwareStatusStyle,
    softwareUpdates,
    capShortId,
    capSiteLabel,
    pendingReplaceMessage,
    sticker,
    unitLifecycle,
    yesNo,
} from "../utils/unitDisplay";

const NOTE_TYPES = ["general", "repair", "warehouse"];

export default function UnitExpandPanel({
    unit,
    cap,
    builderName,
    onUnitPatched,
    onCapsChanged,
}) {
    const [notes, setNotes] = useState(null);
    const [healthLog, setHealthLog] = useState(null);
    const [sim, setSim] = useState(undefined);
    const [noteText, setNoteText] = useState("");
    const [noteType, setNoteType] = useState("general");
    const [savingNote, setSavingNote] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [cycling, setCycling] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const [unactivating, setUnactivating] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [assigningCap, setAssigningCap] = useState(false);
    const [capStickerInput, setCapStickerInput] = useState("");
    const [replacementStickerInput, setReplacementStickerInput] = useState("");
    const [showCapModal, setShowCapModal] = useState(false);
    const [error, setError] = useState(null);
    const skipModalReload = useRef(true);
    const lifecycle = unitLifecycle(unit, cap);
    const updates = softwareUpdates(unit, cap);

    useEffect(() => {
        let cancelled = false;
        setNotes(null);
        setHealthLog(null);
        setSim(undefined);
        setError(null);
        (async () => {
            try {
                const [noteRows, healthRows] = await Promise.all([
                    getUnitNotes(unit.unitId),
                    getUnitHealthLog(unit.unitId).catch(() => []),
                ]);
                let simRow = null;
                try {
                    simRow = await getUnitSim(unit.unitId);
                } catch (err) {
                    if (err?.response?.status !== 404) throw err;
                }
                if (cancelled) return;
                setNotes(Array.isArray(noteRows) ? noteRows : []);
                setHealthLog(Array.isArray(healthRows) ? healthRows : []);
                setSim(simRow && isHardwareSim(simRow) ? simRow : null);
            } catch (err) {
                if (!cancelled) setError(apiErrorMessage(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [unit.unitId]);

    useEffect(() => {
        if (skipModalReload.current) {
            skipModalReload.current = false;
            return;
        }
        if (!showCapModal) {
            onCapsChanged();
        }
    }, [showCapModal]);

    const handleStatus = async (next) => {
        if (next === lifecycle) return;
        if (
            next === "RETIRED" &&
            !window.confirm("Retire this unit? It should not be shipped again.")
        ) {
            return;
        }
        setSavingStatus(true);
        setError(null);
        try {
            const updated = await patchUnit(unit.unitId, {status: next});
            onUnitPatched(updated);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingStatus(false);
    };

    const handleMarkArrived = async () => {
        setSavingStatus(true);
        setError(null);
        try {
            const updated = await patchUnit(unit.unitId, {status: "ARRIVED"});
            onUnitPatched(updated);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingStatus(false);
    };

    const handleCycle = async () => {
        const currentLabel = cap
            ? `${cap.name || sticker(cap.capId)} (${cap.status || "unknown"})`
            : "none";
        if (
            !window.confirm(
                `Return this unit to inventory? Sticker ${sticker(unit.unitId)} stays the same. A new BUILD cap is created for the next customer. Current rental (${currentLabel}) will be decommissioned.`
            )
        ) {
            return;
        }
        setCycling(true);
        setError(null);
        try {
            await createCapForUnit(unit.unitId);
            await onCapsChanged();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setCycling(false);
    };

    const handleAssignExisting = async () => {
        setError(null);
        try {
            const buildCaps = await getCapsAdminOnly(["BUILD"]);
            const {match, matches} = findBySticker(buildCaps, capStickerInput, "capId");
            if (!match) {
                setError(
                    matches.length > 1
                        ? "That sticker matches more than one BUILD cap."
                        : "No BUILD cap found for that sticker."
                );
                return;
            }
            if (match.capId === unit.currentCapId) {
                setError("That cap is already current on this unit.");
                return;
            }
            if (match.unitId && match.unitId !== unit.unitId) {
                if (
                    !window.confirm(
                        `BUILD cap ${sticker(match.capId)} is on unit ${sticker(match.unitId)}. Move it to ${sticker(unit.unitId)}? The current rental on this unit will be decommissioned.`
                    )
                ) {
                    return;
                }
            } else if (
                !window.confirm(
                    `Assign BUILD cap ${sticker(match.capId)} to sticker ${sticker(unit.unitId)}? The current rental will be decommissioned.`
                )
            ) {
                return;
            }
            setAssigningCap(true);
            await assignCapToUnit(unit.unitId, match.capId);
            setCapStickerInput("");
            await onCapsChanged();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setAssigningCap(false);
    };

    const handleReplace = async (replacementUnitId) => {
        if (!unit.currentCapId) return;
        const keepCap = sticker(unit.currentCapId);
        const confirmText = replacementUnitId
            ? `Stage ${sticker(replacementUnitId)} to replace this unit? Cap ${keepCap} stays on this box until the replacement boots and polls assignment. Power the spare off before staging.`
            : `Create a new unit sticker and stage it to replace this one? Cap ${keepCap} stays here until that new box boots.`;
        if (!window.confirm(confirmText)) {
            return;
        }
        setReplacing(true);
        setError(null);
        try {
            const created = await replaceUnit(unit.unitId, replacementUnitId);
            setReplacementStickerInput("");
            if (typeof onCapsChanged === "function") {
                await onCapsChanged(replacementUnitId ? undefined : created);
            }
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setReplacing(false);
    };

    const handleCancelStagedReplace = async () => {
        const spareId = unit.replacesUnitId ? unit.unitId : unit.replacedByUnitId;
        if (!spareId) return;
        if (!window.confirm("Cancel the staged replacement? The field unit keeps its current cap.")) {
            return;
        }
        setReplacing(true);
        setError(null);
        try {
            const updated = await patchUnit(spareId, {replacesUnitId: null});
            if (spareId === unit.unitId && typeof onUnitPatched === "function") {
                onUnitPatched({...updated, replacesUnitId: null, replacedByUnitId: null});
            }
            if (typeof onCapsChanged === "function") {
                await onCapsChanged();
            }
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setReplacing(false);
    };

    const handleReplaceWithSticker = async () => {
        setError(null);
        try {
            const rows = await getUnits();
            const {match, matches} = findBySticker(rows, replacementStickerInput, "unitId");
            if (!match) {
                setError(
                    matches.length > 1
                        ? "That sticker matches more than one unit."
                        : "No unit found for that sticker."
                );
                return;
            }
            if (match.unitId === unit.unitId) {
                setError("Pick a different hardware unit.");
                return;
            }
            await handleReplace(match.unitId);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
    };

    const handleUnactivate = async () => {
        if (!unit.currentCapId) return;
        const capName = cap?.name || capShortId(cap, unit.currentCapId);
        if (
            !window.confirm(
                `Unactivate cap ${capName}?\n\n` +
                    `This will:\n` +
                    `• Set cap status back to BUILD\n` +
                    `• Remove all users who have access to this cap\n` +
                    `• Reset the cap name to the unit sticker (${sticker(unit.unitId)})\n` +
                    `• Clear the activation date and neighborhood\n\n` +
                    `The unit stays on this hardware. Someone will need to activate it again.`
            )
        ) {
            return;
        }
        setUnactivating(true);
        setError(null);
        try {
            await unactivateCap(unit.currentCapId);
            if (typeof onCapsChanged === "function") {
                await onCapsChanged();
            }
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setUnactivating(false);
    };

    const handleScheduleUpdate = async () => {
        if (!unit.currentCapId) return;
        setScheduling(true);
        setError(null);
        const merged = {
            ...updates,
            status: "UPDATE_AVAILABLE",
            update_available: formatUpdateAvailableDate(),
        };
        try {
            await patchCapUpdates(unit.currentCapId, merged);
            onUnitPatched({...unit, updates: merged});
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setScheduling(false);
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        const text = noteText.trim();
        if (!text) return;
        setSavingNote(true);
        setError(null);
        try {
            const payload = {
                unitId: unit.unitId,
                note: text,
                noteType,
            };
            if (unit.currentCapId) payload.capId = unit.currentCapId;
            const created = await createUnitNote(payload);
            setNotes((prev) => [created, ...(prev || [])]);
            setNoteText("");
            setNoteType("general");
            if (typeof onUnitPatched === "function") {
                onUnitPatched({...unit, latestNote: created.note});
            }
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingNote(false);
    };

    const simLabel = sim
        ? [sim.carrier, sim.phoneNumber ? formatPhoneNumber(sim.phoneNumber) || sim.phoneNumber : null, sim.iccid]
              .filter(Boolean)
              .join(" · ")
        : sim === undefined
          ? "Loading…"
          : "None assigned";

    const https = httpsKeysDisplay(updates.https_keys);
    const tzDisp = formatTimezoneDisplay(updates.timezone);

    return (
        <div className="units-expand">
            {error && <div className="units-error">{error}</div>}
            {pendingReplaceMessage(unit) && (
                <div className="units-pending-replace">
                    {pendingReplaceMessage(unit)}
                    <Button
                        bsSize="xsmall"
                        bsStyle="default"
                        style={{marginLeft: 12}}
                        disabled={replacing}
                        onClick={handleCancelStagedReplace}
                    >
                        Cancel replacement
                    </Button>
                </div>
            )}
            <div className="units-expand-grid">
                <div>
                    <span className="units-meta-label">Status</span>
                    <FormControl
                        componentClass="select"
                        value={lifecycle}
                        disabled={savingStatus}
                        onChange={(e) => handleStatus(e.target.value)}
                    >
                        {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </FormControl>
                    {lifecycle === "SHIPPED" && (
                        <Button
                            bsSize="xsmall"
                            bsStyle="warning"
                            style={{marginTop: 8}}
                            disabled={savingStatus}
                            onClick={handleMarkArrived}
                            title="Package arrived at the jobsite; still waiting on customer activation"
                        >
                            Mark arrived
                        </Button>
                    )}
                </div>
                <div>
                    <span className="units-meta-label">Port / SSH</span>
                    <UnitPortField
                        unit={unit}
                        cap={cap}
                        onError={setError}
                        onSaved={(updated) => {
                            if (typeof onUnitPatched === "function") {
                                onUnitPatched(updated);
                            }
                        }}
                    />
                </div>
                <div>
                    <span className="units-meta-label">Architecture</span>
                    <div className="units-meta-value">{unit.architecture || cap?.architecture || "—"}</div>
                </div>
                <div>
                    <span className="units-meta-label">Builder</span>
                    <div className="units-meta-value">{builderName || "—"}</div>
                </div>
                <div>
                    <span className="units-meta-label">Current cap</span>
                    <div className="units-meta-value">
                        {unit.currentCapId ? (
                            <>
                                <Link to={`/caps/${unit.currentCapId}`}>
                                    {capShortId(cap, unit.currentCapId)}
                                </Link>
                                {capSiteLabel(cap) && (
                                    <div className="units-id-muted">{capSiteLabel(cap)}</div>
                                )}
                            </>
                        ) : (
                            "None"
                        )}
                    </div>
                </div>
                <div>
                    <span className="units-meta-label">SIM</span>
                    <div className="units-meta-value">{simLabel}</div>
                </div>
                <div>
                    <span className="units-meta-label">Added</span>
                    <div className="units-meta-value">{formatDate(unit.addDate)}</div>
                </div>
            </div>

            <div className="units-software-grid">
                <div>
                    <span className="units-meta-label">Software</span>
                    <div>
                        {updates.status ? (
                            <Label bsStyle={softwareStatusStyle(updates.status)}>
                                {updates.status === "UPDATE_AVAILABLE" ? "AVAILABLE" : updates.status}
                            </Label>
                        ) : (
                            "—"
                        )}
                    </div>
                </div>
                <div>
                    <span className="units-meta-label">Health</span>
                    <div>
                        {cap?.health ? (
                            <Label bsStyle={healthStyle(cap.health)}>{cap.health}</Label>
                        ) : (
                            "—"
                        )}
                    </div>
                </div>
                <div>
                    <span className="units-meta-label">Updated</span>
                    <div className="units-meta-value">{formatDateOnly(updates.last_updated)}</div>
                </div>
                <div>
                    <span className="units-meta-label">HTTPS keys</span>
                    <div>
                        {https.text === "—" ? "—" : <Label bsStyle={https.bsStyle}>{https.text}</Label>}
                    </div>
                </div>
                <div>
                    <span className="units-meta-label">Hostname</span>
                    <div className="units-meta-value">{updates.hostname || "—"}</div>
                </div>
                <div>
                    <span className="units-meta-label">Timezone</span>
                    <div className="units-meta-value">{tzDisp || "—"}</div>
                </div>
                <div>
                    <span className="units-meta-label">YOLO</span>
                    <div className="units-meta-value">{updates.yolo != null ? updates.yolo : "—"}</div>
                </div>
                <div>
                    <span className="units-meta-label">MP4 / 24/7</span>
                    <div className="units-meta-value">
                        {yesNo(updates.mediamtx)} / {yesNo(updates.footage)}
                    </div>
                </div>
            </div>

            <div className="units-detail-actions">
                {cap && (
                    <Button bsStyle="primary" bsSize="small" onClick={() => setShowCapModal(true)}>
                        <Glyphicon glyph="pencil" /> Assign builder / edit cap
                    </Button>
                )}
                <Button
                    bsStyle="warning"
                    bsSize="small"
                    disabled={cycling || replacing || assigningCap || unactivating}
                    onClick={handleCycle}
                >
                    New rental (keep sticker)
                </Button>
                {unit.currentCapId && (lifecycle === "DEPLOYED" || cap?.status === "ACTIVE") && (
                    <Button
                        bsStyle="danger"
                        bsSize="small"
                        disabled={cycling || replacing || assigningCap || unactivating}
                        onClick={handleUnactivate}
                    >
                        {unactivating ? "Unactivating…" : "Unactivate cap"}
                    </Button>
                )}
                {unit.currentCapId && (
                    <Button
                        bsStyle="danger"
                        bsSize="small"
                        disabled={cycling || replacing || assigningCap || unactivating}
                        onClick={() => handleReplace()}
                    >
                        Stage new replacement unit
                    </Button>
                )}
                {unit.currentCapId && (
                    <Button
                        bsSize="small"
                        bsStyle={updates.status === "UPDATE_AVAILABLE" ? "warning" : "default"}
                        disabled={scheduling || unactivating || updates.status === "UPDATE_AVAILABLE"}
                        onClick={handleScheduleUpdate}
                    >
                        {scheduling ? "Scheduling…" : updates.status === "UPDATE_AVAILABLE" ? "Update scheduled" : "Schedule software update"}
                    </Button>
                )}
                <Link to={`/admin/units/${unit.unitId}`} className="btn btn-default btn-sm">
                    Full unit page
                </Link>
            </div>
            <div className="units-note-form">
                <FormControl
                    type="text"
                    placeholder="Existing BUILD cap sticker"
                    value={capStickerInput}
                    onChange={(e) => setCapStickerInput(e.target.value)}
                    style={{flex: "0 1 220px"}}
                />
                <Button
                    bsStyle="default"
                    bsSize="small"
                    disabled={cycling || replacing || assigningCap || capStickerInput.trim().length < 8}
                    onClick={handleAssignExisting}
                >
                    {assigningCap ? "Assigning…" : "Assign BUILD cap"}
                </Button>
            </div>
            {unit.currentCapId && (
                <div className="units-note-form">
                    <FormControl
                        type="text"
                        placeholder="Replacement unit sticker"
                        value={replacementStickerInput}
                        onChange={(e) => setReplacementStickerInput(e.target.value)}
                        style={{flex: "0 1 220px"}}
                    />
                    <Button
                        bsStyle="danger"
                        bsSize="small"
                        disabled={cycling || replacing || assigningCap || unactivating || replacementStickerInput.trim().length < 8}
                        onClick={handleReplaceWithSticker}
                    >
                        {replacing ? "Staging…" : "Set as replacement"}
                    </Button>
                </div>
            )}

            <UnitSimPanel
                unitId={unit.unitId}
                sim={sim}
                onSimChanged={(next) => setSim(next)}
            />

            <h3 className="units-expand-heading">Notes</h3>
            <form className="units-note-form" onSubmit={handleAddNote}>
                <FormControl
                    componentClass="textarea"
                    placeholder="Hardware note (survives cap cycles)"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                />
                <FormControl
                    componentClass="select"
                    className="units-note-type"
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                >
                    {NOTE_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {type}
                        </option>
                    ))}
                </FormControl>
                <Button type="submit" bsStyle="primary" bsSize="small" disabled={savingNote || !noteText.trim()}>
                    Add note
                </Button>
            </form>
            {notes == null ? (
                <div className="units-empty">Loading notes…</div>
            ) : notes.length === 0 ? (
                <div className="units-empty">No notes yet.</div>
            ) : (
                <Table striped condensed className="units-table">
                    <tbody>
                        {notes.map((note) => (
                            <tr key={note.unitNoteId}>
                                <td>{formatDate(note.addDate)}</td>
                                <td>
                                    <Label bsStyle="default">{note.noteType || "general"}</Label>
                                </td>
                                <td className="units-note-text">{note.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <h3 className="units-expand-heading">Health</h3>
            {healthLog == null ? (
                <div className="units-empty">Loading health…</div>
            ) : healthLog.length === 0 ? (
                <div className="units-empty">No health entries.</div>
            ) : (
                <Table striped condensed className="units-table">
                    <tbody>
                        {healthLog.slice(0, 8).map((entry) => (
                            <tr key={entry.capHealthId}>
                                <td>{formatDate(entry.updateDate || entry.addDate)}</td>
                                <td>{entry.health}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {cap && (
                <CapModal cap={cap} show={showCapModal} setShowCapModal={setShowCapModal} />
            )}
        </div>
    );
}
